/**
 * Cliente HTTP central. Maneja:
 * - Auth header con access_token del store.
 * - Refresh automático en 401 (con cookie httpOnly) y retry una vez.
 * - Parseo de errores JSON con `detail`.
 */
import { useAuthStore } from "@/stores/auth";

const BASE = import.meta.env.VITE_API_BASE || "/api";

export class ApiError extends Error {
  status: number;
  body: any;
  constructor(status: number, message: string, body?: any) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function refresh(): Promise<string | null> {
  const res = await fetch(`${BASE}/auth/refresh`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) return null;
  const body = await res.json();
  useAuthStore.getState().setAccessToken(body.access_token);
  return body.access_token;
}

async function rawFetch(
  path: string,
  init: RequestInit & { _retried?: boolean } = {},
): Promise<Response> {
  const access = useAuthStore.getState().accessToken;
  const headers = new Headers(init.headers || {});
  if (access) headers.set("Authorization", `Bearer ${access}`);
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  const url = path.startsWith("http") ? path : `${BASE}${path}`;
  const res = await fetch(url, { ...init, headers, credentials: "include" });

  if (res.status === 401 && !init._retried && useAuthStore.getState().accessToken) {
    const newAccess = await refresh();
    if (newAccess) {
      return rawFetch(path, { ...init, _retried: true });
    }
    useAuthStore.getState().logout();
  }
  return res;
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const detail = data?.detail || res.statusText;
    throw new ApiError(res.status, typeof detail === "string" ? detail : JSON.stringify(detail), data);
  }
  return data as T;
}

export const api = {
  get: <T,>(path: string) => rawFetch(path).then(parse<T>),
  post: <T,>(path: string, body?: unknown) =>
    rawFetch(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }).then(parse<T>),
  patch: <T,>(path: string, body?: unknown) =>
    rawFetch(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }).then(parse<T>),
  delete: <T,>(path: string) => rawFetch(path, { method: "DELETE" }).then(parse<T>),
};
